import { FocusMonitor } from '@angular/cdk/a11y';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { Component, ElementRef, HostBinding, inject, Input, model, OnDestroy, Optional, Self, signal, SimpleChanges, viewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, FormGroupDirective, NgControl, NgForm, Validators } from '@angular/forms';
import { MatFormField, MatFormFieldControl } from '@angular/material/form-field';
import { Subject } from 'rxjs';

class MyTel {
  constructor(public area: string, public exchange: string) {}
}

@Component({
  selector: 'app-custom-mat-cat',
  templateUrl: './custom-mat-cat.component.html',
  styleUrl: './custom-mat-cat.component.css',
  providers: [{provide: MatFormFieldControl, useExisting: CustomMatCatComponent}],
})
export class CustomMatCatComponent implements MatFormFieldControl<MyTel>, OnDestroy {
  // parts: FormGroup;
  private readonly _elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  touched: boolean;
  errorState: boolean = false;
  describedBy: string;

  @Input()
  get value(): MyTel | null {
    let n = this.parts.value;
    if (n.area.length == 3 && n.exchange.length >= 5 && n.exchange.length <= 10) {
      return new MyTel(n.area, n.exchange);
    }
    return null;
  }

  @Input() disableField: boolean;
  ngOnChanges(changes: SimpleChanges) {
    if (changes.disableField) {
      const control1 = this.parts.get('area');
      const control2 = this.parts.get('exchange');
      if (this.disableField) {
        control1.setValue('');
        control1.disable();
        control2.setValue('');
        control2.disable();
      } else {
        control1.enable();
        control1.addValidators([Validators.minLength(3), Validators.maxLength(3)])
        control1.updateValueAndValidity();
        control2.enable();
        control2.addValidators([Validators.minLength(5), Validators.maxLength(10)])
        control2.updateValueAndValidity();
      }
    }
  }

  stateChanges = new Subject<void>();
  set value(tel: MyTel | null) {
    tel = tel || new MyTel('', '');
    this.parts.setValue({area: tel.area, exchange: tel.exchange});
    this.stateChanges.next();
  }

  constructor(fb: FormBuilder,
  @Optional() private _parentForm: NgForm,
  @Optional() private _parentFormGroup: FormGroupDirective,
  @Optional() public parentFormField: MatFormField) {
    this.parts =  fb.group({
      'area': '',
      'exchange': ''
      // 'subscriber': ''
    });
      // Replace the provider from above with this.
    if (this.ngControl != null) {
      // Setting the value accessor directly (instead of using
      // the providers) to avoid running into a circular import.
      this.ngControl.valueAccessor = this;
    }

    this.parts = inject(FormBuilder).group({
      area: ['', [Validators.minLength(3), Validators.maxLength(3)]],
      exchange: ['', [Validators.minLength(5), Validators.maxLength(10)]]
    });
  }
  required: boolean;
  autofilled?: boolean;
  disableAutomaticLabeling?: boolean;
  readonly _value = model<MyTel | null>(null, {alias: 'value'});
  onChange = (_: any) => {};
  onTouched = () => {};
  private readonly _disabledByCva = signal(false);
  private readonly _focusMonitor = inject(FocusMonitor);
  
  readonly areaInput = viewChild.required<HTMLInputElement>('area');
  readonly exchangeInput = viewChild.required<HTMLInputElement>('exchange');
  ngControl = inject(NgControl, {optional: true, self: true});
  readonly parts: FormGroup<{
    area: FormControl<string | null>;
    exchange: FormControl<string | null>;
  }>;

  ngOnDestroy() {
    this.stateChanges.complete();
  }

  static nextId = 0;
  @HostBinding() id = `app-custom-mat-cat-${CustomMatCatComponent.nextId++}`;

  @Input()
get placeholder() {
  return this._placeholder;
}
set placeholder(plh) {
  this._placeholder = plh;
  this.stateChanges.next();
}
private _placeholder: string;

focused = false;

onFocusIn(event: FocusEvent) {
  if (!this.focused) {
    this.focused = true;
    this.stateChanges.next();
  }
}

onFocusOut(event: FocusEvent) {
  if (!this._elementRef.nativeElement.contains(event.relatedTarget as Element)) {
    this.touched = true;
    this.focused = false;
    this.onTouched();
    this.stateChanges.next();
  }
}

get empty() {
  let n = this.parts.value;
  return !n.area && !n.exchange;
}

@HostBinding('class.floating')
get shouldLabelFloat() {
  return this.focused || !this.empty;
}

private _required = false;
@Input()
getrequired() {
  return this._required;
}
setrequired(req: BooleanInput) {
  this._required = coerceBooleanProperty(req);
  this.stateChanges.next();
}

private _disabled = false;
@Input()
get disabled(): boolean { return this._disabled; }
set disabled(value: BooleanInput) {
  this._disabled = coerceBooleanProperty(value);
  this._disabled ? this.parts.disable() : this.parts.enable();
  this.stateChanges.next();
}

geterrorState(): boolean {
  return this.parts.invalid && this.touched;
}

ngDoCheck() {
  if (this.ngControl) {
    this.updateErrorState();
  }
}

private updateErrorState() {
  const parent = this._parentFormGroup || this._parentForm;

  const oldState = this.errorState;
  const newState = (this.ngControl?.invalid || this.parts.invalid) && (this.touched || parent.submitted);

  if (oldState !== newState) {
    this.errorState = newState;
    this.stateChanges.next();
  }
}

controlType = 'app-custom-mat-cat';

@Input('aria-describedby') userAriaDescribedBy: string;

setDescribedByIds(ids: string[]) {
  const controlElement = this._elementRef.nativeElement
    .querySelector('.app-custom-mat-cat-container')!;
  controlElement.setAttribute('aria-describedby', ids.join(' '));
}

onContainerClick(event: MouseEvent) {
  if ((event.target as Element).tagName.toLowerCase() != 'input') {
    this._elementRef.nativeElement.querySelector('input').focus();
  }
}

writeValue(tel: MyTel | null): void {
  this._updateValue(tel);
}

registerOnChange(fn: any): void {
  this.onChange = fn;
}

registerOnTouched(fn: any): void {
  this.onTouched = fn;
}

setDisabledState(isDisabled: boolean): void {
  this._disabledByCva.set(isDisabled);
}

_handleInput(control: AbstractControl, nextElement?: HTMLInputElement): void {
  this.autoFocusNext(control, nextElement);
  this.onChange(this.value);
}

private _updateValue(tel: MyTel | null) {
  const current = this._value();
  if (
    tel === current ||
    (tel?.area === current?.area &&
      tel?.exchange === current?.exchange)
  ) {
    return;
  }
  this._value.set(tel);
}

autoFocusNext(control: AbstractControl, nextElement?: HTMLInputElement): void {
  if (!control.errors && nextElement) {
    this._focusMonitor.focusVia(nextElement, 'program');
  }
}

autoFocusPrev(control: AbstractControl, prevElement: HTMLInputElement): void {
  if (control.value.length < 1) {
    this._focusMonitor.focusVia(prevElement, 'program');
  }
}

}
